/**
 * Pure transaction-filter helpers used by the Expenses view's advanced search.
 *
 * Kept dependency-free so the filter logic is unit-testable without spinning up
 * Vue components or Firestore mocks. Resolvers for category/account display
 * names are injected so this stays decoupled from any specific store layout.
 */

import type { FirestoreExpense } from "../types/firestore"

export type ExpenseTypeFilter = "all" | "expense" | "income" | "recurring" | "manual" | "bank"

/** Source marker stamped by the Open Banking sync on imported transactions. */
export const BANK_SOURCE = "enablebanking"

/**
 * The value that identifies an expense's account: the linked account id when
 * present (survives account renames), else the legacy free-text label.
 */
export function expenseAccountKey(expense: FirestoreExpense): string {
  return expense.accountId || expense.account
}

export interface AdvancedFilters {
  type: ExpenseTypeFilter
  categories: string[]
  accounts: string[]
  dateFrom: string
  dateTo: string
  amountMin: number | null
  amountMax: number | null
}

export function createEmptyFilters(): AdvancedFilters {
  return {
    type: "all",
    categories: [],
    accounts: [],
    dateFrom: "",
    dateTo: "",
    amountMin: null,
    amountMax: null,
  }
}

/** Number of advanced filters currently in effect, excluding the type filter. */
export function countActiveAdvancedFilters(filters: AdvancedFilters): number {
  let count = 0
  if (filters.categories.length > 0) count++
  if (filters.accounts.length > 0) count++
  if (filters.dateFrom) count++
  if (filters.dateTo) count++
  if (filters.amountMin !== null) count++
  if (filters.amountMax !== null) count++
  return count
}

export interface FilterResolvers {
  /** Map an expense.category value to a human-readable category name. */
  resolveCategoryName: (value: string) => string
  /** Map an expense.account value to a human-readable account name. */
  resolveAccountName: (value: string) => string
}

export function matchesSearchQuery(expense: FirestoreExpense, query: string, resolvers: FilterResolvers): boolean {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) return true
  const description = (expense.description || "").toLowerCase()
  const categoryName = resolvers.resolveCategoryName(expense.category).toLowerCase()
  const accountName = resolvers.resolveAccountName(expenseAccountKey(expense)).toLowerCase()
  const amount = expense.amount.toString()
  return (
    description.includes(trimmed) ||
    categoryName.includes(trimmed) ||
    accountName.includes(trimmed) ||
    amount.includes(trimmed)
  )
}

export function matchesAdvancedFilters(
  expense: FirestoreExpense,
  filters: AdvancedFilters,
  resolvers: FilterResolvers,
): boolean {
  if (filters.type === "recurring") {
    if (!expense.isRecurring) return false
  } else if (filters.type === "bank") {
    if (expense.source !== BANK_SOURCE) return false
  } else if (filters.type === "manual") {
    if (expense.source === BANK_SOURCE) return false
  } else if (filters.type !== "all" && expense.type !== filters.type) {
    return false
  }

  if (filters.categories.length > 0) {
    const name = resolvers.resolveCategoryName(expense.category)
    if (!filters.categories.includes(name)) return false
  }

  if (filters.accounts.length > 0) {
    const name = resolvers.resolveAccountName(expenseAccountKey(expense))
    if (!filters.accounts.includes(name)) return false
  }

  // Date comparisons stay in the YYYY-MM-DD string form — lexical order is
  // identical to chronological order, so we avoid a Date() allocation per row.
  if (filters.dateFrom && expense.date < filters.dateFrom) return false
  if (filters.dateTo && expense.date > filters.dateTo) return false

  if (filters.amountMin !== null && expense.amount < filters.amountMin) return false
  if (filters.amountMax !== null && expense.amount > filters.amountMax) return false

  return true
}

export function filterExpenses(
  expenses: FirestoreExpense[],
  filters: AdvancedFilters,
  searchQuery: string,
  resolvers: FilterResolvers,
): FirestoreExpense[] {
  return expenses.filter(
    (expense) =>
      matchesSearchQuery(expense, searchQuery, resolvers) && matchesAdvancedFilters(expense, filters, resolvers),
  )
}
